/* Dr.Primal — the sticky buy bar.

   Three jobs, and nothing else:

   1. Mirror the buy box. The plan names, save figures, per-serving, billed and price
      strings are all read out of nodes the buy box's Liquid rendered — this file never
      formats a price, so the bar cannot disagree with the box above it in any currency.
   2. Offer the same plan choice. The dropdown is built from the box's own offer cards,
      and choosing one CLICKS that card: the box stays the single owner of the selection,
      and the no-JS form, the guard and the cart path never learn the bar exists.
   3. Stay out of the way while the real Add to basket button is on screen, and carry the
      reader the rest of the way. Adding from the bar is the buy box's own add: the button
      carries `data-dp-buybox-add` and `dp-buy-box.js` handles it, so there is exactly
      one add-to-cart path on the page.

   With no buy box on the page the bar removes itself rather than offering a control that
   cannot work — and it does the same when the buy box on the page sells a different
   product than the bar advertises, which is a broken setting and is logged as one. */
(function () {
  'use strict';

  function init() {
    var bar = document.querySelector('[data-dp-sticky-buy]');
    if (!bar) return;

    var box = document.querySelector('[data-dp-buy-box]');
    var anchor = document.querySelector('[data-dp-buy-submit]');
    if (!box || !anchor) {
      bar.hidden = true;
      return;
    }

    /* The bar and the buy box each carry their own `product` section setting, and on a page
       template both are set by hand. If they name different products the bar advertises one
       title and price while its button — which is the buy box's own add — puts the other
       product's variant in the basket. Nothing downstream catches that: `DPCartGuard`
       reconciles the cart against what the buy box posted, so it agrees with itself and
       raises nothing. Hiding a duplicate control is the safe fallback, but a
       misconfiguration that silently sells the wrong thing is not allowed to be quiet. */
    var barProduct = bar.getAttribute('data-dp-product') || '';
    var boxProduct = box.getAttribute('data-dp-product') || '';
    if (barProduct !== boxProduct) {
      console.error(
        'dp-sticky-buy: the sticky bar is set to product ' +
          (barProduct || '(none)') +
          ' but the buy box on this page sells product ' +
          (boxProduct || '(none)') +
          '. The bar’s button adds the buy box’s product, not the one it advertises, ' +
          'so the bar is hidden. Point both Product settings at the same product.'
      );
      bar.hidden = true;
      return;
    }

    var termsNode = bar.querySelector('[data-dp-sticky-terms]');
    var thumbNode = bar.querySelector('.dp-sticky-buy__thumb');
    var thumbInitial = thumbNode
      ? { src: thumbNode.getAttribute('src'), srcset: thumbNode.getAttribute('srcset') }
      : null;
    var buttonPriceNode = bar.querySelector('[data-dp-sticky-btn-price]');
    var planWrap = bar.querySelector('[data-dp-sticky-plan]');
    var trigger = bar.querySelector('[data-dp-sticky-trigger]');
    var labelNode = bar.querySelector('[data-dp-sticky-plan-label]');
    var servingNode = bar.querySelector('[data-dp-sticky-plan-serving]');
    var list = bar.querySelector('[data-dp-sticky-list]');

    /* "(Save [percent]%)" from the locale file, stamped on the bar by Liquid — no
       English hard-coded here. The percent inside it is lifted out of the buy box's own
       save badge text, so the figure shown is the figure the box shows, never one this
       file computed. */
    var saveTemplate = bar.getAttribute('data-dp-msg-save') || '';

    function saveLabel(badgeText) {
      if (!saveTemplate) return '';
      var match = (badgeText || '').match(/\d+(?:[.,]\d+)?/);
      if (!match) return '';
      return saveTemplate.replace('[percent]', match[0]);
    }

    /* Every string an offer contributes to this bar, read from nodes Liquid rendered in
       the buy box. The `data-dp-for` nodes are read whether hidden or not — hidden only
       means "not the selected offer", the text is still the right text for that offer.
       The badge node shares its `data-dp-for` with the price node inside the same row,
       so the price class is excluded rather than relying on order. */
    function offerStrings(id) {
      var read = function (selector) {
        var node = box.querySelector(selector);
        return node ? node.textContent.trim() : '';
      };
      var label = read('.dp-offer[data-dp-tier-card="' + id + '"] .dp-offer__label');
      if (!label) label = read('.dp-offer-link[data-dp-tier-card="' + id + '"] .dp-offer-link__text');
      return {
        label: label,
        save: read('.dp-buy__price-row [data-dp-node][data-dp-for="' + id + '"]:not(.dp-buy__price)'),
        serving: read('.dp-buy__serving[data-dp-for="' + id + '"]'),
        billed: read('.dp-buy__billed[data-dp-for="' + id + '"]'),
      };
    }

    /* The dropdown lists the plan CARDS only. The de-emphasised one-time option is a
       deliberate design decision in the buy box and stays de-emphasised here: it is not
       offered in the bar, but if the reader selects it in the box the trigger still
       reports it, because the bar's button adds whatever the box has selected. */
    var offers = [];
    Array.prototype.forEach.call(box.querySelectorAll('.dp-offer[data-dp-tier-card]'), function (card) {
      if (card.getAttribute('data-dp-available') !== 'true') return;
      var id = card.getAttribute('data-dp-tier-card');
      var strings = offerStrings(id);
      if (!strings.label) return;
      offers.push({ id: id, card: card, strings: strings, node: null });
    });

    var isOpen = false;

    function setOpen(state) {
      if (!planWrap || planWrap.hidden) return;
      isOpen = state;
      trigger.setAttribute('aria-expanded', state ? 'true' : 'false');
      list.hidden = !state;
      if (state) {
        var selected = list.querySelector('[aria-selected="true"]') || list.firstElementChild;
        if (selected) selected.focus();
      }
    }

    function selectOffer(offer) {
      // The box's own click path: select, validate availability, re-render, announce.
      offer.card.click();
      setOpen(false);
      trigger.focus();
    }

    function optionKeydown(event) {
      var options = Array.prototype.slice.call(list.children);
      var index = options.indexOf(document.activeElement);

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        var delta = event.key === 'ArrowDown' ? 1 : -1;
        var next = options[(index + delta + options.length) % options.length];
        if (next) next.focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        var edge = options[event.key === 'Home' ? 0 : options.length - 1];
        if (edge) edge.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        var offer = offers[index];
        if (offer) selectOffer(offer);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger.focus();
      } else if (event.key === 'Tab') {
        setOpen(false);
      }
    }

    function buildOption(offer) {
      var option = document.createElement('li');
      option.className = 'dp-sticky-buy__plan-option';
      option.setAttribute('role', 'option');
      option.setAttribute('tabindex', '-1');
      option.setAttribute('aria-selected', 'false');

      var name = document.createElement('span');
      name.className = 'dp-sticky-buy__plan-option-name';
      name.textContent = offer.strings.label;
      option.appendChild(name);

      if (offer.strings.serving) {
        var serving = document.createElement('span');
        serving.className = 'dp-sticky-buy__plan-option-serving';
        serving.textContent = offer.strings.serving;
        option.appendChild(serving);
      }

      option.addEventListener('click', function () {
        selectOffer(offer);
      });

      offer.node = option;
      return option;
    }

    if (planWrap && trigger && list && offers.length > 1) {
      offers.forEach(function (offer) {
        list.appendChild(buildOption(offer));
      });
      planWrap.hidden = false;

      trigger.addEventListener('click', function () {
        setOpen(!isOpen);
      });

      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          setOpen(true);
        }
      });

      list.addEventListener('keydown', optionKeydown);

      document.addEventListener('click', function (event) {
        if (!isOpen) return;
        if (event.target instanceof Element && planWrap.contains(event.target)) return;
        setOpen(false);
      });
    }

    /* The trigger reports whichever offer the BOX has selected — the plan cards in the
       dropdown, or the one-time option it does not list — because the bar's button adds
       the box's selection and may never advertise a different one. Every string comes
       from `offerStrings`; `detail` carries the same text and is used for the slots that
       follow the selection (the billed line in the detail column, the price on the
       button). */
    function syncSelection(tier) {
      var id = String(tier || '');
      var current = null;

      offers.forEach(function (offer) {
        var selected = offer.id === id;
        if (offer.node) offer.node.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) current = offer;
      });

      var strings = current ? current.strings : offerStrings(id);
      if (labelNode && strings.label) {
        var save = saveLabel(strings.save);
        labelNode.textContent = save ? strings.label + ' ' + save : strings.label;
      }
      if (servingNode) servingNode.textContent = strings.serving;

      /* The thumb follows the selected VARIANT's own Shopify image (2026-09-03) — not
         the offer card's picture, which comes from a merchant-typed block setting that
         is not guaranteed to match the variant and today repeats one file across two
         different variants. Shopify already holds a genuine, distinct image per variant
         (visible as the thumbnail in the admin variant table), rendered hidden in THIS
         section by Liquid, keyed by variant id via `data-dp-variant-image` — the same
         Liquid-stamps-ids/JS-reads-attributes pattern this theme uses everywhere.

         An earlier version of this fix read `.dp-offer[data-dp-tier-card] .dp-offer__bottle`
         out of the buy box instead — that was itself a fix for a stale `.dp-offer__thumb`
         selector, but it was still reading the CARD's picture, not the VARIANT's, so it
         could never show a real difference between 30-Day and One Time Purchase, which
         share one card image. Reading the variant's own image directly is correct
         regardless of what the card happens to display. A variant with no image at all
         falls back to `thumbInitial`, the product's own photo the bar was served with. */
      if (thumbNode) {
        var variantImg = bar.querySelector('[data-dp-variant-image="' + id + '"] img');
        var next = variantImg
          ? { src: variantImg.getAttribute('src'), srcset: variantImg.getAttribute('srcset') }
          : thumbInitial;
        if (next && next.src && thumbNode.getAttribute('src') !== next.src) {
          thumbNode.setAttribute('src', next.src);
          if (next.srcset) thumbNode.setAttribute('srcset', next.srcset);
          else thumbNode.removeAttribute('srcset');
        }
      }
    }

    /* `detail.price` is the selected offer's PER-MONTH figure, carrying its own `/mo`
       unit because Liquid put the unit inside the same node — so this bar cannot show a
       monthly number without saying it is one. `detail.billed` is the amount actually
       charged and when. Both are strings the buy box read out of nodes Liquid rendered;
       nothing here formats or multiplies anything. */
    document.addEventListener('dp:buybox:change', function (event) {
      var detail = event.detail || {};
      if (detail.tier) syncSelection(detail.tier);
      var terms = detail.billed || detail.plan;
      if (termsNode && terms) termsNode.textContent = terms;
      // No textual separator: the gap between label and figure is the span's own
      // margin in the stylesheet, so narrow and wide labels space identically.
      if (buttonPriceNode) buttonPriceNode.textContent = detail.price || '';
    });

    // Both files are deferred and the buy box is earlier in the document, so its first
    // announcement has already happened by the time this listener exists. Ask for it
    // again rather than starting life with empty slots.
    document.dispatchEvent(new CustomEvent('dp:buybox:sync'));

    // The bar duplicates a control that may already be on screen, so while that control
    // is visible the bar stays out of the way — and out of the tab order.
    function reveal(state) {
      bar.setAttribute('data-dp-revealed', state ? 'true' : 'false');
      if (state) bar.removeAttribute('inert');
      else {
        if (isOpen) setOpen(false);
        bar.setAttribute('inert', '');
      }
    }

    /* Pinned for the whole page when the merchant asks for it (client, 2026-09-03, for
       the PDP). The scroll rule below is then not just bypassed but never wired up —
       leaving the listeners attached would let a later `update()` pull the bar back down
       under exactly the condition the setting exists to override.

       What this knowingly gives up is the reason the rule exists: on the PDP the buy box
       is the FIRST section, so the bar is now pinned over a control group it duplicates,
       and its plan trigger and add button become a second set of tab stops for the same
       three offers. It stays out of `inert` throughout, so both sets are reachable. That
       is the trade the setting makes; it is off by default, and the four landing pages
       leave it off. */
    if (bar.getAttribute('data-dp-always') === 'true') {
      reveal(true);
      return;
    }

    reveal(false);

    /* A scroll listener rather than an IntersectionObserver: the observer only fires when
       the intersection ratio crosses a threshold, so jumping past the button — an anchor
       link, a flick scroll, a restored scroll position — leaves the bar in whatever state
       it was last in. Reading the rectangle is exact, and rAF keeps it to one read a
       frame. */
    var queued = false;

    /* The rule, in one sentence: reveal the bar once the reader has scrolled past the
       button, or — before they reach it — once they are past the first screen and the buy
       box itself is nowhere on screen.

       It is derived from where the buy box actually sits, never from which template is
       being served, so one code path covers both surfaces and neither needs to know the
       other exists.

       On the PDP the buy box is the first section, so it is on screen from the first pixel
       until the button has gone by: only `above` can ever fire, which is the rule this file
       has always had, unchanged at every width. On the landing pages the buy box is nearly
       the last band — `above` alone would reveal the bar over the footer and nowhere else,
       leaving the whole argument with no persistent call to action — so the second clause
       carries those bands and hands back the moment the offer arrives.

       The buy box, not just its button, is what suppresses the bar: the bar duplicates the
       box's plans, prices and add control, and all of them are already in front of the
       reader for the whole height of that band, not only while the button is in view. */
    function update() {
      queued = false;

      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var rect = anchor.getBoundingClientRect();
      var boxRect = box.getBoundingClientRect();
      var scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;

      var above = rect.bottom < 0; // scrolled past the real button
      var boxOnScreen = boxRect.bottom > 0 && boxRect.top < viewport;
      var started = scrolled >= viewport; // past the first screen

      reveal(above || (started && !boxOnScreen));
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
